import api from './api';

export interface LeakedCredential {
  id: number;
  email?: string;
  username?: string;
  password?: string;
  hashed_password?: string;
  hash_type?: string;
  database_name?: string;
  breach_date?: string;
  discovered_date?: string;
  domain: string;
  credential_type: string;
  severity: string;
  status?: string;
  source: string;
  source_reference?: string;
  is_verified: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CredentialBreach {
  id: number;
  name: string;
  title?: string;
  description?: string;
  breach_date?: string;
  discovered_date?: string;
  compromised_accounts?: number;
  compromised_data?: Record<string, any>;
  source: string;
  source_url?: string;
  severity: string;
  is_verified: boolean;
  created_at: string;
}

export interface CredentialInvestigation {
  id: number;
  company_id: number;
  domain: string;
  total_credentials: number;
  unique_emails: number;
  unique_databases: number;
  status: string;
  started_at: string;
  completed_at?: string;
  created_at: string;
}

export interface CredentialStats {
  total_credentials: number;
  unique_emails: number;
  unique_databases: number;
  by_severity: Record<string, number>;
  by_database: Record<string, number>;
  recent_discoveries: number;
}

export interface PaginatedLeakedCredentials {
  data: LeakedCredential[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface StartInvestigationRequest {
  company_id: number;
  domain?: string;
}

export interface ListCredentialsParams {
  company_id?: number;
  domain?: string;
  subdomain?: string;
  database_name?: string;
  severity?: string;
  limit?: number;
  offset?: number;
}

export interface ListBreachesParams {
  limit?: number;
  offset?: number;
}

export interface ListInvestigationsParams {
  company_id?: number;
}

export interface StatsParams {
  company_id?: number;
  domain?: string;
}

class LeakedCredentialsService {
  async listCredentials(params: ListCredentialsParams = {}): Promise<PaginatedLeakedCredentials> {
    const response = await api.get('/api/v2/leaked-credentials/', { params });
    return response.data;
  }

  async getStats(params: StatsParams = {}): Promise<CredentialStats> {
    const response = await api.get('/api/v2/leaked-credentials/stats', { params });
    return response.data;
  }

  async listBreaches(params: ListBreachesParams = {}): Promise<CredentialBreach[]> {
    const response = await api.get('/api/v2/leaked-credentials/breaches', { params });
    return response.data;
  }

  async listInvestigations(params: ListInvestigationsParams = {}): Promise<CredentialInvestigation[]> {
    const response = await api.get('/api/v2/leaked-credentials/investigations', { params });
    return response.data;
  }

  async startInvestigation(request: StartInvestigationRequest): Promise<CredentialInvestigation> {
    const response = await api.post('/api/v2/leaked-credentials/investigate', request);
    return response.data;
  }

  async deleteCredential(credentialId: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/v2/leaked-credentials/${credentialId}`);
    return response.data;
  }

  async updateCredentialStatus(credentialId: number, status: string, domain: string): Promise<LeakedCredential> {
    const response = await api.patch(
      `/api/v2/leaked-credentials/${credentialId}/status`,
      { status },
      { params: { domain } }
    );
    return response.data;
  }

  // Helper methods for formatting and filtering
  formatSeverity(severity: string): string {
    const severityMap: Record<string, string> = {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta',
      critical: 'Crítica',
    };
    return severityMap[severity] || severity;
  }

  getSeverityColor(severity: string): string {
    const colorMap: Record<string, string> = {
      low: 'text-blue-600',
      medium: 'text-yellow-600',
      high: 'text-orange-600',
      critical: 'text-red-600',
    };
    return colorMap[severity] || 'text-gray-600';
  }

  getSeverityBadgeColor(severity: string): string {
    const colorMap: Record<string, string> = {
      low: 'bg-blue-100 text-blue-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800',
    };
    return colorMap[severity] || 'bg-gray-100 text-gray-800';
  }

  formatCredentialType(type: string): string {
    const typeMap: Record<string, string> = {
      email_password: 'Email/Senha',
      username_password: 'Usuário/Senha',
      email_hash: 'Email/Hash',
      username_hash: 'Usuário/Hash',
      api_key: 'Chave API',
      token: 'Token',
      other: 'Outro',
    };
    return typeMap[type] || type;
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Data inválida';
    }
  }

  maskPassword(password?: string): string {
    if (!password) return 'N/A';
    if (password.length <= 4) return '****';
    return password.substring(0, 2) + '*'.repeat(password.length - 4) + password.substring(password.length - 2);
  }

  maskEmail(email?: string): string {
    if (!email) return 'N/A';
    const [localPart, domain] = email.split('@');
    if (!domain) return email;

    if (localPart.length <= 2) {
      return `${localPart}***@${domain}`;
    }

    return `${localPart.substring(0, 2)}***@${domain}`;
  }

  getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      running: 'text-blue-600',
      completed: 'text-green-600',
      failed: 'text-red-600',
    };
    return colorMap[status] || 'text-gray-600';
  }

  getStatusBadgeColor(status: string): string {
    const colorMap: Record<string, string> = {
      running: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  }

  formatStatus(status: string): string {
    const statusMap: Record<string, string> = {
      running: 'Em execução',
      completed: 'Concluída',
      failed: 'Falhou',
    };
    return statusMap[status] || status;
  }
}

export const leakedCredentialsService = new LeakedCredentialsService();
